import {
	Component,
	HostListener,
	Input,
	OnChanges,
	SimpleChanges,
	ChangeDetectionStrategy,
	Output,
	EventEmitter,
	ViewChild,
	ElementRef,
	AfterViewInit,
	NgZone,
} from "@angular/core";
import { DayPilot } from "@daypilot/daypilot-lite-angular";
import { environment } from "src/environments/environment";
import { WoSchedulerComponent } from "../../scheduler.component";
import moment from "moment";
import { DATE_FORMAT } from "src/app/config/constants";
import { DateHelperService } from "src/app/services/date-helper.service";
import _ from "lodash";
import Swal from "sweetalert2";

export interface Schedule {
	id: string;
	name: string;
	startTime: string;
	endTime: string;
	workOrderMainId: string;
	workOrderId: string;
	title: string;
	groupName: string;
	engineerId: string;
	status: string;
}

export type ResourceId = string | number;
export type EventId = string | number;
export type AreaId = string | number;

export interface EventData {
	start: string | DayPilot.Date;
	end: string | DayPilot.Date;
	id: EventId;
	text: string;
	resource?: ResourceId;
	// areas?: AreaData[];
	backColor?: string;
	barBackColor?: string;
	barColor?: string;
	barHidden?: boolean;
	borderColor?: string;
	cssClass?: string;
	fontColor?: string;
	html?: string;
	tags?: any;
}

@Component({
	selector: "app-schedule-grid",
	templateUrl: "./schedule-grid.component.html",
	styleUrls: ["./schedule-grid.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleGridComponent implements OnChanges, AfterViewInit {
	@ViewChild("scheduleContainer") scheduleContainer!: ElementRef<HTMLDivElement>;

	@Input() schedules: Schedule[] = [];
	@Input() selectedStartDate: Date = new Date(
		new Date().getFullYear(),
		new Date().getMonth(),
		1
	);
	@Input() selectedEndDate: Date = new Date(
		new Date().getFullYear(),
		new Date().getMonth() + 1,
		0
	);
	@Output() unscheduleEvent = new EventEmitter<Schedule>();

	timeSlots: Date[] = [];
	dateRange: Date[] = [];
	dateMetaList: { date: Date; dateStr: string; isWeekend: boolean; isHoliday: boolean }[] = [];
	timeSlotMeta: { slot: Date; startMinute: number; endMinute: number }[] = [];
	emptyWeekdaySlots: any[] = [];
	emptyWeekendSlots: any[] = [];
	displayedSchedules: Schedule[] = [];
	mergedTimeHeaders: any[] = [];
	groupedSchedules: any[] = [];

	isConfirmModalVisible: boolean = false;
	selectedSchedule: any = null;

	constructor(
		private woSchedulerComponent: WoSchedulerComponent,
		private dateHelperService: DateHelperService,
		private ngZone: NgZone
	) {}

	ngAfterViewInit() {
		this.scrollToWorkHours();
		this.ngZone.runOutsideAngular(() => {
			if (this.scheduleContainer?.nativeElement) {
				this.scheduleContainer.nativeElement.addEventListener('scroll', () => {
					if (document.activeElement instanceof HTMLElement && document.activeElement.tagName === 'INPUT') {
						document.activeElement.blur();
					}
				}, { passive: true });
			}
		});
	}

	async confirmUnschedule(schedule: Schedule): Promise<void> {
		const result = await Swal.fire({
			title: "Confirm Unschedule",
			text: `Are you sure you want to unschedule ${schedule.workOrderId}?`,
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: "Yes, Unschedule",
			cancelButtonText: "Cancel",
			reverseButtons: true,
			customClass: {
				confirmButton: "swal-confirm-button",
				cancelButton: "swal-cancel-button",
			},
		});

		if (!result.isConfirmed) return;

		this.selectedSchedule = schedule;
		this.isConfirmModalVisible = true;

		this.unscheduleEvent.emit(this.selectedSchedule);
	}

	ngOnChanges(changes: SimpleChanges) {
		if (
			changes.selectedStartDate ||
			changes.selectedEndDate ||
			changes.schedules
		) {
			this.generateDateRange();
			this.generateTimeSlots();
			this.filterSchedulesByDateRange();
			this.mergedTimeHeaders = this.getMergedTimeHeaders();
			this.processGroupedSchedules();
		}
	}

	processGroupedSchedules() {
		const groups = this.groupSchedulesByName();
		for (const group of groups) {
			for (const schedule of group.schedules) {
				schedule.dateSlots = this.dateMetaList.map((dateMeta) => ({
					date: dateMeta.date,
					slots: this.getMergedTimeSlots(schedule, dateMeta),
				}));
			}
		}
		this.groupedSchedules = groups;
		this.scrollToWorkHours();
	}

	scrollToWorkHours() {
		setTimeout(() => {
			if (!this.scheduleContainer?.nativeElement) return;
			const targetHeader = this.scheduleContainer.nativeElement.querySelector(
				".time-header-8am"
			) as HTMLElement;
			if (targetHeader) {
				const scrollLeft = targetHeader.offsetLeft - 220;
				this.scheduleContainer.nativeElement.scrollTo({
					left: Math.max(0, scrollLeft),
					behavior: "smooth",
				});
			}
		}, 150);
	}

	trackByDate(index: number, date: Date): string {
		return date.toString();
	}

	trackByGroup(index: number, group: any): string {
		return group.name || index.toString();
	}

	trackBySchedule(index: number, schedule: any): string {
		return schedule.id || schedule.workOrderId || index.toString();
	}

	filterSchedulesByDateRange() {
		this.displayedSchedules = this.schedules || [];
	}

	generateDateRange() {
		this.dateRange = [];
		this.dateMetaList = [];
		let currentDate = new Date(this.selectedStartDate);
		const endDate = new Date(this.selectedEndDate);
		endDate.setHours(0, 0, 0, 0);

		while (currentDate <= endDate) {
			const d = new Date(currentDate);
			const dayOfWeek = d.getDay();
			const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
			const dateStr = moment(d).format(DATE_FORMAT);
			const isHoliday = this.checkIsHoliday(dateStr);

			this.dateRange.push(d);
			this.dateMetaList.push({
				date: d,
				dateStr: dateStr,
				isWeekend: isWeekend,
				isHoliday: isHoliday,
			});
			currentDate.setDate(currentDate.getDate() + 1);
		}
	}

	generateTimeSlots() {
		this.timeSlots = [];
		this.timeSlotMeta = [];
		this.emptyWeekdaySlots = [];
		this.emptyWeekendSlots = [];

		const start = new Date(this.selectedStartDate);
		start.setHours(0, 0, 0, 0);

		for (let i = 0; i < 96; i++) {
			const slotTime = new Date(start);
			slotTime.setMinutes(i * 15);
			this.timeSlots.push(slotTime);

			const startMinute = i * 15;
			const endMinute = (i + 1) * 15 - 1;

			this.timeSlotMeta.push({
				slot: slotTime,
				startMinute,
				endMinute,
			});

		}

		this.emptyWeekdaySlots = [{
			start: this.timeSlots[0],
			colspan: 96,
			isScheduled: false,
			isFirst: true,
			isWeekend: false,
		}];

		this.emptyWeekendSlots = [{
			start: this.timeSlots[0],
			colspan: 96,
			isScheduled: false,
			isFirst: true,
			isWeekend: true,
		}];
	}

	private checkIsHoliday(dateStr: string): boolean {
		if (!this.woSchedulerComponent?.HOLIDAYS?.length) return false;
		for (const item of this.woSchedulerComponent.HOLIDAYS) {
			const start = item["holiday_start_date"];
			const end = item["holiday_end_date"];
			if (start && end && start <= dateStr && dateStr <= end) {
				return !!item.display_in_calendar;
			}
		}
		return false;
	}

	getMergedTimeHeaders() {
		const mergedHeaders = [];
		let currentHeader = null;

		for (let slot of this.timeSlots) {
			const hour = slot.getHours();

			if (!currentHeader || currentHeader.hour !== hour) {
				if (currentHeader) {
					mergedHeaders.push(currentHeader);
				}
				currentHeader = {
					start: slot,
					hour: hour,
					colspan: 1,
					isFirst: true,
				};
			} else {
				currentHeader.colspan++;
			}
		}

		if (currentHeader) {
			mergedHeaders.push(currentHeader);
		}

		return mergedHeaders;
	}

	getMergedTimeSlots(schedule: Schedule, dateMeta: any) {
		const isWeekend = dateMeta.isWeekend;

		if (!schedule.startTime || !schedule.endTime) {
			return [{
				start: this.timeSlots[0],
				colspan: 96,
				isScheduled: false,
				isFirst: true,
				isWeekend: isWeekend,
			}];
		}

		// Fast check using YYYY-MM-DD substring
		const eventDateStr = schedule.startTime.substring(0, 10);
		if (eventDateStr !== dateMeta.dateStr) {
			return [{
				start: this.timeSlots[0],
				colspan: 96,
				isScheduled: false,
				isFirst: true,
				isWeekend: isWeekend,
			}];
		}

		// Fast time parsing: "YYYY-MM-DD HH:mm:ss"
		const startHour = parseInt(schedule.startTime.substring(11, 13), 10);
		const startMin = parseInt(schedule.startTime.substring(14, 16), 10);
		const endHour = parseInt(schedule.endTime.substring(11, 13), 10);
		const endMin = parseInt(schedule.endTime.substring(14, 16), 10);

		if (isNaN(startHour) || isNaN(endHour)) {
			return [{
				start: this.timeSlots[0],
				colspan: 96,
				isScheduled: false,
				isFirst: true,
				isWeekend: isWeekend,
			}];
		}

		const eventStartMinute = startHour * 60 + startMin;
		const eventEndMinute = endHour * 60 + endMin;

		const mergedSlots = [];
		let currentSlot = null;

		for (const slotMeta of this.timeSlotMeta) {
			const isScheduled =
				slotMeta.endMinute >= eventStartMinute &&
				slotMeta.startMinute <= eventEndMinute;

			if (isScheduled) {
				if (currentSlot) {
					currentSlot.colspan++;
				} else {
					currentSlot = {
						start: slotMeta.slot,
						colspan: 1,
						isScheduled: true,
						isFirst: true,
						isWeekend,
						schedule,
					};
				}
			} else {
				if (currentSlot) {
					mergedSlots.push(currentSlot);
					currentSlot = null;
				}
				mergedSlots.push({
					start: slotMeta.slot,
					colspan: 1,
					isScheduled: false,
					isFirst: true,
					isWeekend,
				});
			}
		}

		if (currentSlot) {
			mergedSlots.push(currentSlot);
		}

		return mergedSlots;
	}

	groupSchedulesByName() {
		const grouped = this.displayedSchedules.reduce((acc: any, schedule) => {
			const groupName =
				schedule.groupName && schedule.groupName.trim() !== ""
					? schedule.groupName
					: "";

			const existingGroup = acc.find(
				(g: any) => g.name === schedule.name
			);

			if (existingGroup) {
				existingGroup.schedules.push(schedule);
			} else {
				acc.push({
					name: schedule.name,
					group: groupName,
					schedules: [schedule],
				});
			}

			return acc;
		}, []);

		return grouped.sort((a: any, b: any) => {
			if (a.group === "") return -1;
			if (b.group === "") return 1;
			return a.group.localeCompare(b.group);
		});
	}

	handleCellClick(slot: any, date: Date, schedule: Schedule): void {
		this.viewWorkOrder(schedule);
	}

	viewWorkOrder(wo) {
		window.open(
			`${environment.BASE_URL}/#/work-orders/${wo.workOrderMainId}`,
			"_blank"
		);
	}
}
